import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { merge, fromEvent, timer } from 'rxjs'
import { map, distinctUntilChanged, tap, takeUntil, skip, startWith } from 'rxjs/operators'
import { sound } from '@schmancy/audio'
import '../surface/surface'

const ONLINE_CUE = 'connectivity-online'
const OFFLINE_CUE = 'connectivity-offline'

// Resolved at first play, so an import beside a non-hierarchical URL never throws.
sound.registerSample(ONLINE_CUE, () => new URL('./sounds/online.wav', import.meta.url).href)
sound.registerSample(OFFLINE_CUE, () => new URL('./sounds/offline.wav', import.meta.url).href)

/** Monitors online/offline state and shows an animated banner on change. */
@customElement('schmancy-connectivity-status')
export class SchmancyConnectivityStatus extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}

			.banner {
				transform: translateY(-100%);
				opacity: 0;
				transition:
					transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1),
					opacity 400ms ease-out;
			}

			.banner.visible {
				transform: translateY(0);
				opacity: 1;
			}

			.banner.exiting {
				transform: translateY(-100%);
				opacity: 0;
				transition:
					transform 300ms ease-out,
					opacity 300ms ease-out;
			}

			@keyframes icon-pulse {
				0%,
				100% {
					transform: scale(1);
				}
				50% {
					transform: scale(1.2);
				}
			}

			@keyframes icon-bounce {
				0%,
				100% {
					transform: translateY(0);
				}
				25% {
					transform: translateY(-6px);
				}
				50% {
					transform: translateY(0);
				}
				75% {
					transform: translateY(-3px);
				}
			}

			.icon-pulse {
				animation: icon-pulse 1s ease-in-out infinite;
			}

			.icon-bounce {
				animation: icon-bounce 600ms ease-out;
			}
		`,
	]
	private bannerRef = createRef<HTMLDivElement>()
	private surfaceRef = createRef<HTMLElement>()
	private iconRef = createRef<HTMLSpanElement>()
	private messageRef = createRef<HTMLSpanElement>()

	connectedCallback(): void {
		super.connectedCallback()

		merge(
			fromEvent(window, 'online').pipe(map(() => true)),
			fromEvent(window, 'offline').pipe(map(() => false)),
		)
			.pipe(
				startWith(navigator.onLine),
				distinctUntilChanged(),
				// Skip the mount state, preserving the first actual connectivity change.
				skip(1),
				tap(online => this.updateBanner(online)),
				// Background changes must not queue stale audio before the next gesture.
				tap(online => sound.playCue(online ? ONLINE_CUE : OFFLINE_CUE, { dropIfSuspended: true })),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	private updateBanner(online: boolean): void {
		const banner = this.bannerRef.value
		const surface = this.surfaceRef.value
		const icon = this.iconRef.value
		const message = this.messageRef.value

		if (!banner || !surface || !icon || !message) return

		if (!online) {
			surface.setAttribute('type', 'error')
			icon.textContent = '🙀'
			icon.className = 'text-2xl icon-pulse'
			message.textContent = "You're offline"
			banner.classList.remove('exiting')
			banner.classList.add('visible')
		} else {
			surface.setAttribute('type', 'primary')
			icon.textContent = '🎉'
			icon.className = 'text-2xl icon-bounce'
			message.textContent = 'Back online'
			banner.classList.remove('exiting')
			banner.classList.add('visible')

			timer(1500)
				.pipe(
					tap(() => {
						banner.classList.add('exiting')
						timer(300)
							.pipe(
								tap(() => banner.classList.remove('visible', 'exiting')),
								takeUntil(this.disconnecting),
							)
							.subscribe()
					}),
					takeUntil(this.disconnecting),
				)
				.subscribe()
		}
	}

	protected render(): unknown {
		return html`
			<div ${ref(this.bannerRef)} class="banner fixed top-0 inset-x-0 z-50 p-2 pointer-events-none">
				<schmancy-surface
					${ref(this.surfaceRef)}
					type="error"
					class="mx-auto max-w-sm rounded-2xl shadow-lg pointer-events-auto"
				>
					<div class="flex items-center gap-3 px-4 py-3">
						<span ${ref(this.iconRef)} class="text-2xl">🙀</span>
						<schmancy-typography type="body" token="md">
							<span ${ref(this.messageRef)}>You're offline</span>
						</schmancy-typography>
					</div>
				</schmancy-surface>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-connectivity-status': SchmancyConnectivityStatus
	}
}
